/*
 * Investment Dropdown
 * */

import classNames from 'classnames';
import PropTypes from 'prop-types';
// BASE
import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';

import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowRightRoundedIcon from '@mui/icons-material/ArrowRightRounded';
import { MenuItem } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
// MUI COMPONENTS
import Typography from '@mui/material/Typography';

// CUSTOM COMPONENTS
import SimplifiStyledSelect from 'components/SimplifiSelects/SimplifiStyledSelect';

import InvestmentBottomButtons from './InvestmentBottomButtons';
// PATH RELATIVE IMPORTS
import useStyles from './styles';

/* Properties */
//  setIsAllSelected: Simply sets a true or false value if every item in the list is selected or not
//  instantFilter: Use this if you want the selectedItems to change RIGHT when the checkmark is clicked rather than having to click a 'confirm' button. (This is slow and bad)
//  showAll: A toggle that lets you choose whether or not the 'All' option at the top appears ever
//  selectedItems: expects an array of menuArray item values
//  onSelected: returns array of all currently selected menuArray item values

/* Component */
const SimplifiMultiSelect = (props) => {
  const { classes } = useStyles();
  const cancelFocus = useRef(null);
  const {
    menuArray,
    onSelected,
    name,
    selectedItems,
    showAll,
    startDroppedDown,
    instantFilter,
    setIsAllSelected,
    ...other
  } = props;

  const [mySelectedItems, setMySelectedItems] = useState(selectedItems || []);
  const [mySavedSelectedItems, setMySavedSelectedItems] = useState(selectedItems || []);
  const [anchorEl, setAnchorEl] = useState(null);
  const [shownItems, setShownItems] = useState(new Array(menuArray.length).fill(startDroppedDown));
  const [headerLabels, setHeaderLabels] = useState(menuArray.map((item) => item.name));
  const [itemArray, setItemArray] = useState(menuArray.map((item) => item.items));

  /*
   * Get the full length by counting the length of the arrays inside the main array
   * */
  const getItemArrayLength = useCallback(() => {
    let fullLength = 0;
    for (let i = 0; i < itemArray.length; i++) {
      fullLength += itemArray[i].length;
    }
    return fullLength;
  }, [itemArray]);

  useEffect(() => {
    setShownItems(new Array(menuArray.length).fill(startDroppedDown));
  }, [menuArray.length, startDroppedDown]);

  useEffect(() => {
    setHeaderLabels(menuArray.map((item) => item.name));
    setItemArray(menuArray.map((item) => item.items));
  }, [menuArray]);

  useEffect(() => {
    if (selectedItems) {
      setMySavedSelectedItems(selectedItems);
      setMySelectedItems(selectedItems);
    } else {
      setMySavedSelectedItems([]);
      setMySelectedItems([]);
    }
  }, [selectedItems]);

  const allItemsKey = '0B2F7296-D641-45DC-BBD0-14168635D69E';
  const open = Boolean(anchorEl);

  /*
   * Checks if SOME of a group of items are selected
   * */
  const containsSomeItems = useCallback(
    (items) => items.some((r) => mySelectedItems.indexOf(r) >= 0),
    [mySelectedItems],
  );

  /*
   * Checks if ALL items passed down are selected
   * */
  const containsAllItems = useCallback(
    (items) => {
      for (let i = 0; i < items.length; i++) {
        if (!mySelectedItems.includes(items[i])) return false;
      }
      return true;
    },
    [mySelectedItems],
  );

  /*
   * Takes all the items in the arrays of the array and puts them into a single unsorted array
   * */
  const getItemsInSingleArray = useMemo(() => {
    const newArray = [];
    itemArray.forEach((accountType) => {
      accountType?.forEach((item) => {
        newArray.push(item);
      });
    });
    return newArray;
  }, [itemArray]);

  const applyFilter = useCallback(
    (e) => {
      e?.stopPropagation();
      onSelected(mySelectedItems);
      setMySavedSelectedItems(mySelectedItems);

      if (mySelectedItems.length === getItemArrayLength() && setIsAllSelected) {
        setIsAllSelected(true);
      } else if (setIsAllSelected) {
        setIsAllSelected(false);
      }

      setAnchorEl(null);
    },
    [getItemArrayLength, mySelectedItems, onSelected, setIsAllSelected],
  );

  const cancelFilter = useCallback(
    (e) => {
      //  Exit select menu OR tab to cancel OR tab to Confirm
      if (e.key !== 'Tab') {
        e?.stopPropagation();
        setAnchorEl(null);
        setMySelectedItems(mySavedSelectedItems);
      } else if (cancelFocus !== document.activeElement) {
        cancelFocus.current.focus();
      }
    },
    [mySavedSelectedItems],
  );

  const removeUndefined = useCallback((itemList) => itemList.filter((item) => item !== undefined), []);

  const eraseDuplicates = useCallback((itemList) => {
    const newList = itemList.filter(
      (account, index, self) => index === self.findIndex((duplicate) => duplicate.id === account.id),
    );
    return newList;
  }, []);

  const onAccountChange = (e) => {
    //  console.log('REMOVED VALUES: ', removeUndefined(e.target.value));
    let newSelectedItems = removeUndefined(e.target.value);

    if (newSelectedItems.includes(allItemsKey)) {
      //  If user selected *all*
      if (mySelectedItems.length >= getItemArrayLength()) {
        //  And ALL other items were already selected
        newSelectedItems = [];
      } else {
        //  And NOT ALL other items were already selected
        newSelectedItems = getItemsInSingleArray;
      }
    } else if (newSelectedItems) {
      for (let i = 0; i < headerLabels.length; i++) {
        if (newSelectedItems.includes(headerLabels[i])) {
          //  If this headerlabel was in the list
          //  Remove the text item from newSelectedItems
          const savedLength = newSelectedItems.length;
          for (let j = savedLength - 1; j >= 0; j--) {
            if (headerLabels.includes(newSelectedItems[j])) {
              newSelectedItems.splice(j, 1);
            }
          }
          if (containsAllItems(itemArray[i])) {
            const storedLength = newSelectedItems.length;
            for (let j = storedLength; j >= 0; j--) {
              if (itemArray[i].includes(newSelectedItems[j])) {
                newSelectedItems.splice(j, 1);
              }
            }
          } else {
            newSelectedItems = newSelectedItems.concat(itemArray[i]);
          }
        }
      }
    } else {
      newSelectedItems = [];
    }

    newSelectedItems = eraseDuplicates(newSelectedItems);
    setMySelectedItems(newSelectedItems);
    if (typeof onSelected !== 'undefined' && instantFilter) {
      onSelected(newSelectedItems);
    }
  };

  const accountRenderValue = useCallback(
    (renderSelectedItems) => {
      const tempSelectedItems = removeUndefined(renderSelectedItems);

      if (tempSelectedItems.length === 1) {
        return `${tempSelectedItems[0].nickname || tempSelectedItems[0].name}${'isClosed' in tempSelectedItems[0] && tempSelectedItems[0].isClosed ? ' (Closed)' : ''}`;
      } else if (tempSelectedItems.length === getItemArrayLength()) {
        return 'All accounts';
      } else if (tempSelectedItems.length !== 0) {
        return (
          <div className={classes.textContainer}>
            <div className={classes.numItems}>
              <Typography className={classes.innerNum}>{tempSelectedItems.length}</Typography>
            </div>
            <Typography className={classes.textBox}>{name.toUpperCase()}</Typography>
          </div>
        );
      }
      return 'None selected';
    },
    [classes, getItemArrayLength, name, removeUndefined],
  );

  // need to memoize menuProps if you don't want the paper to stutter on close animation
  const menuProps = useMemo(
    () => ({
      classes: {
        paper: classNames(classes.menuPaper, anchorEl ? '' : 'closed'),
        list: classes.menuList,
      },
      disableAutoFocusItem: true,
    }),
    [anchorEl, classes],
  );

  return (
    <SimplifiStyledSelect
      showZeroState={!menuArray.length}
      onOpen={(e) => {
        setAnchorEl(e.currentTarget);
      }}
      MenuProps={menuProps}
      id='account-select-dropdown'
      open={open}
      multiple
      value={mySelectedItems}
      onClose={cancelFilter}
      onChange={onAccountChange}
      displayEmpty
      renderValue={(renderSelectedItems) => accountRenderValue(renderSelectedItems)}
      {...other}
    >
      {showAll && itemArray && getItemArrayLength() > 1 && (
        <MenuItem
          key={allItemsKey}
          value={allItemsKey}
          id={name}
          classes={{ selected: classes.selectedMenuItem }}
          className={classes.allAccountsPadding}
        >
          <IconButton
            id='all-toggle'
            aria-label='Expand or Collapse'
            className={classes.dropdownIconAllAccts}
            key='all-toggle'
            disabled
            onClick={(e) => {
              e?.stopPropagation();
              setShownItems([]);
            }}
            size='large'
          >
            {shownItems ? <ArrowDropDownRoundedIcon /> : <ArrowRightRoundedIcon />}
          </IconButton>
          <Checkbox
            checked={mySelectedItems.length === getItemArrayLength()}
            color='primary'
            indeterminate={Boolean(mySelectedItems.length && mySelectedItems.length !== getItemArrayLength())}
          />
          <ListItemText primary={`All ${name}`} />
        </MenuItem>
      )}

      {itemArray &&
        itemArray.map((list, index) => {
          const arr = [];
          const subMenuItem = list.map((item) => (
            <MenuItem
              key={item.nickname || item.name}
              value={item}
              className={classes.subMenuItem}
              classes={{ selected: classes.selectedMenuItem }}
            >
              <Checkbox checked={mySelectedItems.includes(item)} color='primary' />
              <ListItemText
                id={`${item.id}-item-${item.nickname || item.name}`}
                primary={
                  <Typography color={'textSecondary'} sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
                    {item.nickname || item.name}
                    {'isClosed' in item && item.isClosed ? (
                      <Typography component='span' variant='captionRegular' className={classes.closedAccountLabel}>
                        (Closed)
                      </Typography>
                    ) : null}
                  </Typography>
                }
              />
            </MenuItem>
          ));
          const parentMenuItem = (
            <MenuItem
              key={`header-${headerLabels[index]}`}
              value={headerLabels[index]}
              className={classes.headerMenuItem}
              classes={{ selected: classes.selectedMenuItem }}
            >
              <IconButton
                id={`${headerLabels[index]}-toggle`}
                aria-label='Expand or Collapse'
                className={classes.dropdownIcon}
                key={`${headerLabels[index]}-toggle`}
                onClick={(e) => {
                  //  console.log('OLD STATE: ', shownItems);
                  e?.stopPropagation();
                  const newCollapse = !shownItems[index];
                  const newShownItems = shownItems;
                  newShownItems.splice(index, 1, newCollapse);
                  setShownItems([...newShownItems]);
                  //  console.log('NEW STATE: ', shownItems);
                }}
                size='large'
              >
                {shownItems[index] ? <ArrowDropDownRoundedIcon /> : <ArrowRightRoundedIcon />}
              </IconButton>
              <Checkbox
                checked={containsAllItems(itemArray[index])}
                color='primary'
                indeterminate={!containsAllItems(itemArray[index]) && Boolean(containsSomeItems(itemArray[index]))}
              />
              <ListItemText id={`item-${index}-text`} primary={headerLabels[index]} />
            </MenuItem>
          );
          if (list?.length) {
            arr.push(parentMenuItem);
            if (shownItems[index]) {
              arr.push(subMenuItem);
              if (index !== itemArray.length - 1) {
                arr.push(<Divider className={classes.divider} />);
              }
            }
          }
          return arr;
        })}
      <InvestmentBottomButtons
        mySelectedItems={mySelectedItems}
        cancelFocus={cancelFocus}
        cancelFilter={cancelFilter}
        applyFilter={applyFilter}
      />
    </SimplifiStyledSelect>
  );
};

SimplifiMultiSelect.defaultProps = {
  name: 'Accounts',
  startDroppedDown: true,
};
SimplifiMultiSelect.propTypes = {
  startDroppedDown: PropTypes.bool,
  menuArray: PropTypes.array, // expects array of headers with selectable 'items' subArray
  instantFilter: PropTypes.bool,
  onSelected: PropTypes.func.isRequired, // returns array of all selected item values
  name: PropTypes.string.isRequired,
  selectedItems: PropTypes.array, // expects array of selected item values
  showAll: PropTypes.bool,
  setIsAllSelected: PropTypes.func,
  viewState: PropTypes.string,
  sharedcomponentid: PropTypes.string,
};

export default SimplifiMultiSelect;
